import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { Tabs, TabsList, TabsTrigger, TabsContent, StatusBadge, DataTable } from '@/components/ui';
import RestaurantModal from '@/components/RestaurantModal';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import type { Column } from '@/components/ui';
import type { RestaurantInGroup, User, RestaurantGroup, RestaurantGroupUpdateRequest } from '@/types/api';
import { TerminalStatus } from '@/types/api';
import { formatTimeAgo } from '@/utils/time';
import {
  ArrowLeft,
  Plus,
  Settings,
  RefreshCw,
  Building2,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';

interface RestaurantSyncSummary {
  pendingOutbox: number;
  pendingInbox: number;
  conflicts: number;
  online: boolean;
  hasTerminals: boolean;
  lastSeen?: string;
}

/**
 * Real per-restaurant sync/terminal data, replacing the restaurant.syncStatus
 * the API never actually populated (report 13's C3). Needs sync.read +
 * terminal.read; callers must gate on `enabled` and fall back to an explicit
 * "Unknown" state rather than treating a missing summary as "Offline".
 */
function useRestaurantSyncSummaries(restaurants: RestaurantInGroup[] | undefined, enabled: boolean) {
  return useQueries({
    queries: (restaurants ?? []).map((restaurant) => ({
      queryKey: ['restaurant-sync-summary', restaurant.id],
      queryFn: async (): Promise<RestaurantSyncSummary> => {
        const [status, terminals, inboxPage] = await Promise.all([
          apiClient.getSyncStatus(restaurant.id),
          apiClient.getTerminals(restaurant.id),
          apiClient.getSyncInbox(restaurant.id, 'PENDING', 0, 1),
        ]);
        const lastSeen = terminals.reduce<string | undefined>((latest, t) => {
          if (!t.lastHeartbeat) return latest;
          if (!latest || new Date(t.lastHeartbeat) > new Date(latest)) return t.lastHeartbeat;
          return latest;
        }, undefined);
        return {
          pendingOutbox: status.pendingCount,
          pendingInbox: inboxPage.totalElements,
          conflicts: status.conflictCount,
          online: terminals.some((t) => t.status === TerminalStatus.ONLINE),
          hasTerminals: terminals.length > 0,
          lastSeen,
        };
      },
      enabled: enabled && !!restaurant.id,
      staleTime: 30_000,
    })),
  });
}

export default function GroupDetailPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const [activeTab, setActiveTab] = useState('restaurants');

  const canUpdate = useHasPermission(PERM.GROUP_UPDATE);
  const canReadSync = useHasPermission(PERM.SYNC_READ);
  const canReadTerminals = useHasPermission(PERM.TERMINAL_READ);
  const canViewSyncDetails = canReadSync && canReadTerminals;

  const {
    data: group,
    isLoading: groupLoading,
    isError: groupError,
    refetch: refetchGroup,
  } = useQuery({
    queryKey: ['group', groupId],
    queryFn: () => apiClient.getGroup(Number(groupId)),
    enabled: !!groupId,
  });

  const {
    data: restaurants,
    isLoading: restaurantsLoading,
    isError: restaurantsError,
    refetch: refetchRestaurants,
  } = useQuery({
    queryKey: ['group-restaurants', groupId],
    queryFn: () => apiClient.getGroupRestaurants(Number(groupId)),
    enabled: !!groupId,
  });

  const {
    data: users,
    isLoading: usersLoading,
    isError: usersError,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ['group-users', groupId],
    queryFn: () => apiClient.getGroupUsers(Number(groupId)),
    enabled: !!groupId,
  });

  const syncSummaryQueries = useRestaurantSyncSummaries(restaurants, canViewSyncDetails);

  if (groupLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (groupError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <div className="text-gray-900 font-medium">Failed to load restaurant group</div>
        <button onClick={() => refetchGroup()} className="btn btn-outline h-10">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Group not found</p>
        <Link to="/groups" className="btn btn-outline mt-4 h-10">
          Back to Groups
        </Link>
      </div>
    );
  }

  const syncSummaryByRestaurantId = new Map<number, RestaurantSyncSummary>();
  (restaurants ?? []).forEach((restaurant, i) => {
    const summary = syncSummaryQueries[i]?.data;
    if (summary) syncSummaryByRestaurantId.set(restaurant.id, summary);
  });
  const conflictCount = canViewSyncDetails
    ? Array.from(syncSummaryByRestaurantId.values()).reduce((sum, s) => sum + s.conflicts, 0)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/groups"
            className="p-2 hover:bg-gray-100 rounded-md text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{group.name}</h1>
            {group.description && (
              <p className="text-sm text-gray-500 mt-1">{group.description}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {canUpdate && (
            <button
              onClick={() => setActiveTab('settings')}
              className="btn btn-outline h-10"
            >
              <Settings className="h-4 w-4 mr-2" />
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="restaurants" value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="restaurants" badge={restaurants?.length}>
            Restaurants
          </TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="users" badge={users?.length}>
            Users
          </TabsTrigger>
          <TabsTrigger value="sync" badge={conflictCount > 0 ? conflictCount : undefined}>
            Sync Status
          </TabsTrigger>
        </TabsList>

        <TabsContent value="restaurants" className="mt-6">
          <RestaurantsTab
            groupId={Number(groupId)}
            restaurants={restaurants}
            isLoading={restaurantsLoading}
            isError={restaurantsError}
            onRetry={refetchRestaurants}
            canViewSyncDetails={canViewSyncDetails}
            syncSummaryByRestaurantId={syncSummaryByRestaurantId}
          />
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <SettingsTab group={group} />
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <UsersTab
            users={users}
            isLoading={usersLoading}
            isError={usersError}
            onRetry={refetchUsers}
          />
        </TabsContent>

        <TabsContent value="sync" className="mt-6">
          <SyncStatusTab
            groupId={Number(groupId)}
            restaurants={restaurants}
            conflictCount={conflictCount}
            canViewSyncDetails={canViewSyncDetails}
            syncSummaryByRestaurantId={syncSummaryByRestaurantId}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Restaurants Tab
interface RestaurantsTabProps {
  groupId: number;
  restaurants?: RestaurantInGroup[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canViewSyncDetails: boolean;
  syncSummaryByRestaurantId: Map<number, RestaurantSyncSummary>;
}

function RestaurantsTab({
  groupId,
  restaurants,
  isLoading,
  isError,
  onRetry,
  canViewSyncDetails,
  syncSummaryByRestaurantId,
}: RestaurantsTabProps) {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const canManage = useHasPermission(PERM.GROUP_MANAGE);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  if (isLoading) {
    return <div className="text-gray-500">Loading restaurants...</div>;
  }

  if (isError) {
    return (
      <div className="card p-8 text-center">
        <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-3" />
        <p className="text-gray-900 font-medium mb-3">Failed to load restaurants</p>
        <button onClick={onRetry} className="btn btn-outline h-10">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">
          Restaurants ({restaurants?.length ?? 0})
        </h2>
        {canManage && (
          <button onClick={openModal} className="btn btn-primary h-10">
            <Plus className="h-4 w-4 mr-2" />
            Add Restaurant
          </button>
        )}
      </div>

      {restaurants && restaurants.length > 0 ? (
        <div className="card divide-y divide-gray-200">
          {restaurants.map((restaurant) => {
            const summary = syncSummaryByRestaurantId.get(restaurant.id);
            return (
              <div
                key={restaurant.id}
                className="p-4 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex items-center gap-4">
                  <Building2 className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">{restaurant.name}</p>
                    <p className="text-sm text-gray-500">
                      {restaurant.city}, {restaurant.state} | {restaurant.maxTerminals} terminals
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    {!canViewSyncDetails ? (
                      <StatusBadge status="offline" label="Unknown" />
                    ) : !summary ? (
                      <StatusBadge status="offline" label="Loading..." />
                    ) : !summary.hasTerminals ? (
                      <StatusBadge status="offline" label="No terminals" />
                    ) : (
                      <StatusBadge
                        status={summary.online ? 'online' : 'offline'}
                        label={summary.online ? 'Online' : 'Offline'}
                      />
                    )}
                    {summary?.hasTerminals && (
                      <p className="text-xs text-gray-500 mt-1">
                        Last seen: {formatTimeAgo(summary.lastSeen)}
                      </p>
                    )}
                  </div>
                  <Link
                    to={`/restaurants/${restaurant.id}/tables`}
                    className="btn btn-outline h-9 text-sm"
                  >
                    Manage
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card p-8 text-center">
          <Building2 className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No restaurants in this group yet</p>
          {canManage && (
            <button onClick={openModal} className="btn btn-primary mt-4 h-10">
              Add First Restaurant
            </button>
          )}
        </div>
      )}

      {isModalOpen && (
        <RestaurantModal
          restaurant={null}
          defaultGroupId={groupId}
          onClose={closeModal}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['group-restaurants'] });
            closeModal();
          }}
        />
      )}
    </div>
  );
}

// Settings Tab
interface SettingsTabProps {
  group: RestaurantGroup;
}

function SettingsTab({ group }: SettingsTabProps) {
  const queryClient = useQueryClient();
  const canUpdate = useHasPermission(PERM.GROUP_UPDATE);
  // Only real, persisted RestaurantGroupUpdateRequest fields -- the previous
  // defaultCurrency/defaultTimezone/shareMenuItems/centralisedUserManagement/
  // requireMenuApproval fields don't exist anywhere in the backend data model
  // (report 12's C2) and were silently discarded on every save (C1). Name is
  // immutable via this endpoint, same as GroupModal on GroupsPage.
  const [formData, setFormData] = useState<RestaurantGroupUpdateRequest>({
    description: group.description || '',
    addressLine1: group.addressLine1 || '',
    addressLine2: group.addressLine2 || '',
    city: group.city || '',
    state: group.state || '',
    zipCode: group.zipCode || '',
    country: group.country || '',
    billingAddressLine1: group.billingAddressLine1 || '',
    billingAddressLine2: group.billingAddressLine2 || '',
    billingCity: group.billingCity || '',
    billingState: group.billingState || '',
    billingZipCode: group.billingZipCode || '',
    billingCountry: group.billingCountry || '',
  });
  const [submitError, setSubmitError] = useState<string | null>(null);

  const updateMutation = useMutation({
    mutationFn: (data: RestaurantGroupUpdateRequest) => apiClient.updateGroup(group.id, data),
    onSuccess: () => {
      setSubmitError(null);
      queryClient.invalidateQueries({ queryKey: ['group', group.id.toString()] });
      // Also invalidate the list GroupsPage reads (H2) -- a name/description
      // change saved here previously left the card grid showing stale data
      // until an unrelated refetch.
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to save changes';
      setSubmitError(message);
    },
  });

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  return (
    <div className="max-w-2xl">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Group Settings</h2>

      {submitError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {submitError}
        </div>
      )}

      <div className="card p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Group Name
          </label>
          <input
            type="text"
            value={group.name}
            disabled
            className="input w-full bg-gray-50 text-gray-500"
          />
          <p className="mt-1 text-xs text-gray-500">Group name cannot be changed after creation.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="input w-full"
            rows={2}
          />
        </div>

        {/* Address */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street Address
              </label>
              <input
                type="text"
                value={formData.addressLine1}
                onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                className="input w-full"
                placeholder="123 High Street"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address Line 2
              </label>
              <input
                type="text"
                value={formData.addressLine2}
                onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City / Town
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="input w-full"
                placeholder="London"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                County
              </label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="input w-full"
                placeholder="Greater London"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postcode
              </label>
              <input
                type="text"
                value={formData.zipCode}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                className="input w-full"
                placeholder="SW1A 1AA"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="input w-full"
                placeholder="United Kingdom"
              />
            </div>
          </div>
        </div>

        {/* Billing Address */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Billing Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street Address
              </label>
              <input
                type="text"
                value={formData.billingAddressLine1}
                onChange={(e) => setFormData({ ...formData, billingAddressLine1: e.target.value })}
                className="input w-full"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address Line 2
              </label>
              <input
                type="text"
                value={formData.billingAddressLine2}
                onChange={(e) => setFormData({ ...formData, billingAddressLine2: e.target.value })}
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City / Town
              </label>
              <input
                type="text"
                value={formData.billingCity}
                onChange={(e) => setFormData({ ...formData, billingCity: e.target.value })}
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                County
              </label>
              <input
                type="text"
                value={formData.billingState}
                onChange={(e) => setFormData({ ...formData, billingState: e.target.value })}
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postcode
              </label>
              <input
                type="text"
                value={formData.billingZipCode}
                onChange={(e) => setFormData({ ...formData, billingZipCode: e.target.value })}
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <input
                type="text"
                value={formData.billingCountry}
                onChange={(e) => setFormData({ ...formData, billingCountry: e.target.value })}
                className="input w-full"
              />
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200">
          {canUpdate && (
            <button
              onClick={handleSave}
              className="btn btn-primary h-10"
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          )}
          {updateMutation.isSuccess && !submitError && (
            <span className="ml-3 text-sm text-green-600">Changes saved!</span>
          )}
        </div>
      </div>
    </div>
  );
}

// Users Tab
interface UsersTabProps {
  users?: User[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

function UsersTab({ users, isLoading, isError, onRetry }: UsersTabProps) {
  if (isLoading) {
    return <div className="text-gray-500">Loading users...</div>;
  }

  if (isError) {
    return (
      <div className="card p-8 text-center">
        <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-3" />
        <p className="text-gray-900 font-medium mb-3">Failed to load users</p>
        <button onClick={onRetry} className="btn btn-outline h-10">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  const columns: Column<User>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (user: User) => (
        <div>
          <p className="font-medium text-gray-900">
            {user.firstName} {user.lastName}
          </p>
          <p className="text-sm text-gray-500">{user.email}</p>
        </div>
      ),
    },
    {
      key: 'roles',
      header: 'Role',
      render: (user: User) => (
        <span className="badge badge-primary">
          {user.roles?.[0]?.name || 'Staff'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user: User) => (
        <StatusBadge
          status={user.active ? 'online' : 'offline'}
          label={user.active ? 'Active' : 'Inactive'}
        />
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">
          Users & Access ({users?.length ?? 0} users)
        </h2>
      </div>

      {users && users.length > 0 ? (
        <DataTable<User>
          data={users}
          columns={columns}
          keyExtractor={(user) => String(user.id)}
          pageSize={10}
        />
      ) : (
        <div className="card p-8 text-center">
          <p className="text-gray-500">No users in this group yet</p>
        </div>
      )}
    </div>
  );
}

// Sync Status Tab
interface SyncStatusTabProps {
  groupId: number;
  restaurants?: RestaurantInGroup[];
  conflictCount: number;
  canViewSyncDetails: boolean;
  syncSummaryByRestaurantId: Map<number, RestaurantSyncSummary>;
}

function SyncStatusTab({
  groupId,
  restaurants,
  conflictCount,
  canViewSyncDetails,
  syncSummaryByRestaurantId,
}: SyncStatusTabProps) {
  const queryClient = useQueryClient();
  const canManage = useHasPermission(PERM.GROUP_MANAGE);

  const forceSyncMutation = useMutation({
    mutationFn: async (targets: RestaurantInGroup[]) => {
      const results = await Promise.allSettled(
        targets.map((r) => apiClient.forceSyncRestaurant(r.id))
      );
      return targets
        .filter((_, i) => results[i].status === 'rejected')
        .map((r) => r.name);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group', groupId.toString()] });
      queryClient.invalidateQueries({ queryKey: ['group-restaurants', groupId.toString()] });
    },
  });

  const handleForceSync = () => {
    if (restaurants && restaurants.length > 0) {
      forceSyncMutation.mutate(restaurants);
    }
  };

  const failedNames = forceSyncMutation.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">Sync Status</h2>
        {canManage && (
          <button
            onClick={handleForceSync}
            className="btn btn-outline h-10"
            disabled={forceSyncMutation.isPending}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${forceSyncMutation.isPending ? 'animate-spin' : ''}`} />
            Force Sync All
          </button>
        )}
      </div>

      {forceSyncMutation.isError && (
        <div className="card p-4 bg-red-50 border-red-200 text-sm text-red-700">
          Failed to trigger sync for one or more restaurants. Please try again.
        </div>
      )}

      {!forceSyncMutation.isError && failedNames.length > 0 && (
        <div className="card p-4 bg-red-50 border-red-200 text-sm text-red-700">
          Failed to sync: {failedNames.join(', ')}
        </div>
      )}

      {!canViewSyncDetails && (
        <div className="card p-4 bg-gray-50 border-gray-200 text-sm text-gray-600">
          You don't have permission to view live sync details for these restaurants.
        </div>
      )}

      {/* Summary Card */}
      {conflictCount > 0 && (
        <div className="card p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            <div>
              <p className="font-medium text-yellow-800">
                {conflictCount} conflict{conflictCount !== 1 ? 's' : ''} need resolution
              </p>
              <p className="text-sm text-yellow-700">
                Review and resolve conflicts to ensure data consistency
              </p>
            </div>
            <Link to={`/sync?groupId=${groupId}`} className="btn btn-outline h-9 ml-auto">
              View Conflicts
            </Link>
          </div>
        </div>
      )}

      {/* Restaurant Sync Table */}
      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                Restaurant
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-gray-500">
                Outbox
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-gray-500">
                Inbox
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-gray-500">
                Conflicts
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-gray-500">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {restaurants?.map((restaurant) => {
              const summary = syncSummaryByRestaurantId.get(restaurant.id);
              const statusLabel = !canViewSyncDetails
                ? 'Unknown'
                : !summary
                ? 'Loading...'
                : !summary.hasTerminals
                ? 'No terminals'
                : summary.online
                ? 'OK'
                : 'Offline';
              const statusType = canViewSyncDetails && summary?.online ? 'online' : 'offline';

              return (
                <tr key={restaurant.id}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {restaurant.name}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-500">
                    {summary ? summary.pendingOutbox : '—'}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-500">
                    {summary ? summary.pendingInbox : '—'}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-500">
                    {summary ? summary.conflicts : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge
                      status={statusType}
                      label={statusLabel}
                      size="sm"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
